/**
 * Overlay MPR oblicuo: líneas de intersección coloreadas + hit-test de handles.
 * Geometría en espacio de índices; dibujo en pixeles de imagen (col,row).
 */

import { cross, normalize, planeFrame, planeFrameForDisplay } from './mprOblique'

export const MPR_PLANE_COLORS = {
  axial: '#FFEB3B',
  coronal: '#42A5F5',
  sagittal: '#EF5350',
}

/** Planos cuyas intersecciones se dibujan en un viewport dado. */
export function otherPlaneRoles(viewportRole) {
  const r = String(viewportRole || 'axial').toLowerCase()
  if (r === 'coronal') return ['axial', 'sagittal']
  if (r === 'sagittal') return ['axial', 'coronal']
  return ['coronal', 'sagittal']
}

/**
 * Dirección 2D (dc, dr) en la imagen del viewport para la línea del plano `lineRole`.
 * @param {number[]} matrix
 * @param {string} viewportRole
 * @param {string} lineRole
 * @returns {{ dx: number, dy: number }|null}
 */
export function intersectionDirInImage(matrix, viewportRole, lineRole) {
  const vp = planeFrameForDisplay(matrix, viewportRole)
  const other = planeFrame(matrix, lineRole)
  const dir3 = normalize(cross(vp.n, other.n))
  if (!dir3[0] && !dir3[1] && !dir3[2]) return null
  // Proyectar al plano de imagen (u,v)
  let dx = dir3[0] * vp.u[0] + dir3[1] * vp.u[1] + dir3[2] * vp.u[2]
  let dy = dir3[0] * vp.v[0] + dir3[1] * vp.v[1] + dir3[2] * vp.v[2]
  const len = Math.sqrt(dx * dx + dy * dy)
  if (!(len > 1e-8)) return null
  return { dx: dx / len, dy: dy / len }
}

/**
 * Distancia positiva desde el centro hasta el borde del rect (rayo unitario).
 * @param {number} cx
 * @param {number} cy
 * @param {number} dx
 * @param {number} dy
 * @param {number} width
 * @param {number} height
 */
export function rayHitViewportEdge(cx, cy, dx, dy, width, height) {
  const w = Math.max(1, Number(width) || 1)
  const h = Math.max(1, Number(height) || 1)
  const ts = []
  if (Math.abs(dx) > 1e-9) {
    ts.push((0 - cx) / dx)
    ts.push((w - cx) / dx)
  }
  if (Math.abs(dy) > 1e-9) {
    ts.push((0 - cy) / dy)
    ts.push((h - cy) / dy)
  }
  const pos = ts.filter((t) => t > 1e-6)
  if (!pos.length) return Math.min(w, h) * 0.5
  return Math.min.apply(null, pos)
}

/**
 * Segmento borde-a-borde del viewport (como reference lines H/V al 100%).
 * Usa distancias independientes +dir / −dir para que el centro descentrado
 * (letterbox / imagen no centrada en CSS) no acorte el brazo opuesto.
 * @param {number} cx
 * @param {number} cy
 * @param {{ dx: number, dy: number }} dir
 * @param {number} width
 * @param {number} height
 */
export function lineSegmentViewportEdges(cx, cy, dir, width, height) {
  const dx = Number(dir && dir.dx) || 0
  const dy = Number(dir && dir.dy) || 0
  const tPos = rayHitViewportEdge(cx, cy, dx, dy, width, height)
  const tNeg = rayHitViewportEdge(cx, cy, -dx, -dy, width, height)
  return {
    cx,
    cy,
    x1: cx - dx * tNeg,
    y1: cy - dy * tNeg,
    x2: cx + dx * tPos,
    y2: cy + dy * tPos,
    halfLen: Math.min(tPos, tNeg),
    tPos,
    tNeg,
  }
}

/**
 * Segmento clipado al rectángulo de la imagen (modelo en píxeles DICOM).
 * @param {{ outCols: number, outRows: number }} out
 * @param {{ dx: number, dy: number }} dir
 * @returns {{ x1: number, y1: number, x2: number, y2: number, cx: number, cy: number }}
 */
export function lineSegmentThroughCenter(out, dir) {
  const cols = Math.max(1, Number(out && out.outCols) || 1)
  const rows = Math.max(1, Number(out && out.outRows) || 1)
  const cx = (cols - 1) / 2
  const cy = (rows - 1) / 2
  return lineSegmentViewportEdges(cx, cy, dir, cols - 1, rows - 1)
}

/**
 * Handles de rotación a lo largo de una línea (fracción del menor lado).
 * @param {{ cx: number, cy: number }} center
 * @param {{ dx: number, dy: number }} dir
 * @param {{ outCols: number, outRows: number }} out
 * @param {number} [frac]
 */
export function rotateHandlePixels(center, dir, out, frac = 0.32) {
  const cols = Math.max(1, Number(out && out.outCols) || 1)
  const rows = Math.max(1, Number(out && out.outRows) || 1)
  const r = Math.min(cols, rows) * frac
  return [
    { x: center.cx + dir.dx * r, y: center.cy + dir.dy * r },
    { x: center.cx - dir.dx * r, y: center.cy - dir.dy * r },
  ]
}

/**
 * Modelo completo de overlay para un viewport.
 * @param {{
 *   matrix: number[],
 *   viewportRole: string,
 *   outCols: number,
 *   outRows: number,
 * }} args
 */
export function buildObliqueOverlayModel(args) {
  const matrix = args.matrix
  const viewportRole = String(args.viewportRole || 'axial').toLowerCase()
  const out = { outCols: args.outCols, outRows: args.outRows }
  const lines = []
  for (const role of otherPlaneRoles(viewportRole)) {
    const dir = intersectionDirInImage(matrix, viewportRole, role)
    if (!dir) continue
    const seg = lineSegmentThroughCenter(out, dir)
    const handles = rotateHandlePixels(seg, dir, out)
    lines.push({
      role,
      color: MPR_PLANE_COLORS[role] || '#FFFFFF',
      ...seg,
      dir,
      handles,
    })
  }
  const cx = (out.outCols - 1) / 2
  const cy = (out.outRows - 1) / 2
  return {
    viewportRole,
    center: { x: cx, y: cy },
    centerColor: MPR_PLANE_COLORS[viewportRole] || '#FFEB3B',
    lines,
  }
}

function dist2(ax, ay, bx, by) {
  const dx = ax - bx
  const dy = ay - by
  return dx * dx + dy * dy
}

/**
 * Hit-test en coords de imagen (col,row).
 * @param {ReturnType<typeof buildObliqueOverlayModel>} model
 * @param {{ x: number, y: number }} pixel
 * @param {{ centerRadius?: number, handleRadius?: number }} [opts]
 * @returns {{ kind: 'center'|'rotate', role?: string }|null}
 */
export function hitObliqueOverlayModel(model, pixel, opts = {}) {
  if (!model || !pixel) return null
  const cr = Number(opts.centerRadius) || 14
  const hr = Number(opts.handleRadius) || 16
  const px = Number(pixel.x)
  const py = Number(pixel.y)
  if (dist2(px, py, model.center.x, model.center.y) <= cr * cr) {
    return { kind: 'center' }
  }
  for (const line of model.lines || []) {
    for (const h of line.handles || []) {
      if (dist2(px, py, h.x, h.y) <= hr * hr) {
        return { kind: 'rotate', role: line.role }
      }
    }
  }
  // Cerca del eje de una línea (fuera del centro) → rotar ese plano
  const lineHitR = Number(opts.lineRadius) || 10
  for (const line of model.lines || []) {
    const dx = line.dir && line.dir.dx
    const dy = line.dir && line.dir.dy
    if (dx == null || dy == null) continue
    const vx = px - line.cx
    const vy = py - line.cy
    const along = vx * dx + vy * dy
    const perp = vx * -dy + vy * dx
    const minAlong = Math.min(outLen(model) * 0.12, 20)
    if (Math.abs(perp) <= lineHitR && Math.abs(along) >= minAlong) {
      return { kind: 'rotate', role: line.role }
    }
  }
  return null
}

function outLen(model) {
  const lines = model.lines
  if (!lines || !lines[0]) return 100
  return Math.sqrt(
    (lines[0].x2 - lines[0].x1) ** 2 + (lines[0].y2 - lines[0].y1) ** 2
  )
}
